/**
 * TickerBar - Phase 15
 * Scrolling market data ticker at the top of the app
 */
import { useQuery } from '@tanstack/react-query'
import { TrendingUp, TrendingDown } from 'lucide-react'
import clsx from 'clsx'
import './TickerBar.css'

interface TickerItem {
  symbol: string
  price: number
  change: number
  changePercent: number
}

export function TickerBar() {
  // Fetch global market data
  const { data: marketData } = useQuery({
    queryKey: ['ticker-bar-markets'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/macro/global-markets')
        if (!response.ok) return null
        return response.json()
      } catch {
        return null
      }
    },
    refetchInterval: 60000,
    staleTime: 30000,
  })

  // Fetch movers data (reserved for future use)
  const { data: _moversData } = useQuery({
    queryKey: ['ticker-bar-movers'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/market/movers')
        if (!response.ok) return null
        return response.json()
      } catch {
        return null
      }
    },
    refetchInterval: 60000,
    staleTime: 30000,
  })

  // Build ticker items from available data
  const tickerItems: TickerItem[] = []

  // Add index data if available - fallback to mock data if API fails
  const indices = marketData?.indices || {
    SPY: { price: 512.34, change: 0.82, changePercent: 0.82 },
    QQQ: { price: 438.21, change: 1.23, changePercent: 1.23 },
    DIA: { price: 398.67, change: 0.45, changePercent: 0.45 },
    IWM: { price: 201.89, change: -0.32, changePercent: -0.32 },
    VIX: { price: 14.23, change: -0.89, changePercent: -0.89 },
  }

  // Add main indices
  const mainSymbols = ['SPY', 'QQQ', 'DIA', 'IWM', 'VIX']
  mainSymbols.forEach(symbol => {
    const data = indices[symbol]
    if (data) {
      tickerItems.push({
        symbol,
        price: data.price || data.last || 0,
        change: data.change || 0,
        changePercent: data.changePercent || data.change_pct || 0,
      })
    }
  })

  // Add crypto if available
  const crypto = marketData?.crypto || {}
  if (crypto['BTC-USD']) {
    tickerItems.push({
      symbol: 'BTC',
      price: crypto['BTC-USD'].price || 67500,
      change: crypto['BTC-USD'].change || 1.5,
      changePercent: crypto['BTC-USD'].changePercent || 1.5,
    })
  }

  // Add gold if available
  const commodities = marketData?.commodities || {}
  if (commodities.gold) {
    tickerItems.push({
      symbol: 'GOLD',
      price: commodities.gold.price || 2345.67,
      change: commodities.gold.change || 0.3,
      changePercent: commodities.gold.changePercent || 0.3,
    })
  }

  // Fallback data if no real data available
  if (tickerItems.length === 0) {
    tickerItems.push(
      { symbol: 'SPY', price: 512.34, change: 0.82, changePercent: 0.82 },
      { symbol: 'QQQ', price: 438.21, change: 1.23, changePercent: 1.23 },
      { symbol: 'DIA', price: 398.67, change: 0.45, changePercent: 0.45 },
      { symbol: 'IWM', price: 201.89, change: -0.32, changePercent: -0.32 },
      { symbol: 'VIX', price: 14.23, change: -0.89, changePercent: -0.89 },
      { symbol: 'BTC', price: 67542.00, change: 2.15, changePercent: 2.15 },
      { symbol: 'GOLD', price: 2345.67, change: 0.34, changePercent: 0.34 },
    )
  }

  // Duplicate for seamless loop
  const allItems = [...tickerItems, ...tickerItems]

  const formatPrice = (price: number) => {
    if (price >= 10000) return price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    if (price >= 100) return price.toFixed(2)
    return price.toFixed(2)
  }

  const formatChange = (change: number) => {
    const prefix = change >= 0 ? '+' : ''
    return `${prefix}${change.toFixed(2)}%`
  }

  return (
    <div className="ticker-bar">
      <div className="ticker-bar-track">
        {allItems.map((item, index) => (
          <div key={`${item.symbol}-${index}`} className="ticker-bar-item">
            <span className="ticker-symbol">{item.symbol}</span>
            <span className="ticker-price">{formatPrice(item.price)}</span>
            <span className={clsx('ticker-change', item.change >= 0 ? 'positive' : 'negative')}>
              {item.change >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
              {formatChange(item.changePercent)}
            </span>
            <span className="ticker-divider">|</span>
          </div>
        ))}
      </div>
    </div>
  )
}
